import { useRef, useState } from "react";
import { FileText, Loader2, Sparkles, Upload } from "lucide-react";
import { createNevisignDocument } from "@nevisign/reader-core";
import { useAuth } from "../contexts/auth-context";
import {
  createContractFromIntake,
  readFileAsTextAttachment,
} from "../lib/platform-contracts";
import {
  newDraftId,
  removeLocalDraft,
  upsertLocalDraft,
  type LocalDraft,
} from "../lib/local-drafts";

type Attachment = { name: string; content: string; mime?: string };

type Props = {
  apiBase: string;
  onBack: () => void;
  onOpenContract: (contractId: string, title: string) => void;
  onOpenLocalDraft: (draft: LocalDraft) => void;
};

const DEFAULT_TITLE = "قرارداد هوش مصنوعی";

export function ContractAiCreatePage({
  apiBase,
  onBack,
  onOpenContract,
}: Props) {
  const { isAuthenticated, openLogin } = useAuth();
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [pendingDraftId, setPendingDraftId] = useState<string | null>(null);
  const [loginSaveOpen, setLoginSaveOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function addFiles(list: FileList | null) {
    if (!list?.length) return;
    setError(null);
    setAttaching(true);
    try {
      const next = [...attachments];
      for (const file of Array.from(list)) {
        if (next.length >= 5) {
          setError("حداکثر ۵ فایل می‌توانید اضافه کنید");
          break;
        }
        if (next.some((a) => a.name === file.name)) continue;
        try {
          next.push(await readFileAsTextAttachment(file));
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : `خواندن فایل «${file.name}» ناموفق بود`,
          );
        }
      }
      setAttachments(next);
    } finally {
      setAttaching(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function persistClientDraft(content: string, draftId?: string): LocalDraft {
    const id = draftId ?? pendingDraftId ?? newDraftId();
    const draft = upsertLocalDraft({
      id,
      kind: "ai",
      title: DEFAULT_TITLE,
      text: content,
      attachments,
    });
    setPendingDraftId(id);
    return draft;
  }

  async function runGenerate() {
    const combined =
      text.trim().length +
      attachments.reduce((n, a) => n + a.content.trim().length, 0);
    if (combined < 10) {
      setError("متن یا فایل کافی بنویسید / اضافه کنید.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!isAuthenticated) {
        const draft = persistClientDraft(text);
        openLogin(() => {
          void runGenerateAfterLogin(draft.id);
        });
        setBusy(false);
        return;
      }
      await runGenerateAfterLogin(pendingDraftId ?? undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "خطا در تولید");
      setBusy(false);
    }
  }

  async function runGenerateAfterLogin(draftId?: string) {
    setBusy(true);
    setError(null);
    try {
      // Same endpoint as panel DocumentCreatePage → createContractFromIntake
      // (POST /api/v1/contracts/from-intake). Do not fall back to POST /documents —
      // that only pastes text into a blank contract and skips the AI pipeline.
      const contract = await createContractFromIntake(apiBase, {
        title: DEFAULT_TITLE,
        sourceText: text,
        fileTexts: attachments.length ? attachments : undefined,
      });
      const preview =
        text.trim() ||
        attachments.map((a) => a.content).join("\n\n") ||
        contract.progressMessage ||
        "قرارداد در حال تولید توسط عوامل هوش مصنوعی پلتفرم است.";
      setGeneratedText(preview);
      persistClientDraft(preview, draftId);
      if (draftId) removeLocalDraft(draftId);
      setLoginSaveOpen(false);
      onOpenContract(contract.id, contract.title || DEFAULT_TITLE);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "خطا در ایجاد سند با هوش مصنوعی";
      setError(msg);
      const draft = persistClientDraft(
        text.trim() || attachments.map((a) => a.content).join("\n\n"),
        draftId,
      );
      setGeneratedText(draft.text);
      setLoginSaveOpen(!isAuthenticated);
    } finally {
      setBusy(false);
    }
  }

  async function saveGeneratedToAccount() {
    if (!generatedText?.trim()) return;
    if (!isAuthenticated) {
      openLogin(() => void saveGeneratedToAccount());
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createNevisignDocument(apiBase, {
        mode: "import",
        title: DEFAULT_TITLE,
        text: generatedText,
      });
      if (pendingDraftId) removeLocalDraft(pendingDraftId);
      setLoginSaveOpen(false);
      onOpenContract(created.id, DEFAULT_TITLE);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ذخیره ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flow-page consumer-shell">
      <header className="flow-page-head">
        <button type="button" className="text-back" onClick={onBack}>
          ← بازگشت
        </button>
        <span className="consumer-kicker">همراه با هوش مصنوعی</span>
        <h1>ایجاد سند از طریق هوش مصنوعی</h1>
        <p>
          شرح قرارداد را بنویسید یا فایل‌های مرجع بدهید تا سرویس عوامل پلتفرم مسیر
          تدوین را شروع کند — همان API پنل نویساین.
        </p>
        <div className="hero-art">
          <img
            src="/illustrations/service-draft.webp"
            alt="تصویر تدوین قرارداد با هوش مصنوعی"
          />
        </div>
      </header>

      <form
        className="ai-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          void runGenerate();
        }}
      >
        <label htmlFor="ai-brief">شرح مختصر</label>
        <textarea
          id="ai-brief"
          rows={10}
          dir="rtl"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="طرفین، موضوع، مبلغ، مهلت و شرایط مهم را بنویسید…"
        />
        <div className="consumer-chip-row">
          <span className="consumer-chip">حداکثر ۵ فایل مرجع</span>
          <span className="consumer-chip">مناسب برای پیش‌نویس اولیه</span>
        </div>
        <div className="ai-create-toolbar">
          <input
            ref={fileRef}
            type="file"
            multiple
            className="sr-only"
            tabIndex={-1}
            accept=".txt,.md,.csv,.json,.html,.htm,.xml,.doc,.docx,.pdf,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => void addFiles(e.target.files)}
          />
          <button
            type="button"
            className="ghost-btn"
            disabled={busy || attaching || attachments.length >= 5}
            onClick={() => fileRef.current?.click()}
          >
            {attaching ? (
              <Loader2 size={15} className="spin" />
            ) : (
              <Upload size={15} />
            )}
            {attaching ? "در حال خواندن…" : "افزودن فایل"}
          </button>
          <span className="settings-hint">{attachments.length}/۵ فایل</span>
        </div>
        {attachments.length ? (
          <ul className="attach-chip-list">
            {attachments.map((a) => (
              <li key={a.name}>
                <FileText size={12} />
                {a.name}
                <button
                  type="button"
                  aria-label="حذف"
                  onClick={() =>
                    setAttachments((prev) => prev.filter((x) => x.name !== a.name))
                  }
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <button type="submit" className="primary-btn wide" disabled={busy || attaching}>
          {busy ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
          {busy ? "در حال شروع…" : "شروع تولید"}
        </button>
      </form>

      {error ? <p className="reader-login-error">{error}</p> : null}

      {generatedText ? (
        <section className="ai-result-card">
          <h2>پیش‌نویس تولید / آماده ذخیره</h2>
          <pre className="ai-result-text">{generatedText.slice(0, 4000)}</pre>
          <button
            type="button"
            className="primary-btn"
            disabled={busy}
            onClick={() => void saveGeneratedToAccount()}
          >
            ذخیره در حساب
          </button>
        </section>
      ) : null}

      {loginSaveOpen ? (
        <div className="sheet-backdrop" role="presentation">
          <div className="sheet-card" role="dialog">
            <h2>ذخیره قرارداد</h2>
            <p>برای ذخیره قرارداد باید به حساب کاربری خود وارد شوید.</p>
            <div className="sheet-actions">
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setLoginSaveOpen(false)}
              >
                بعداً
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={() => openLogin(() => void saveGeneratedToAccount())}
              >
                ورود
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
